import { getLabelMessage } from '@gef-ui/localization/messages';
import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import isNotNil from "ramda-extension/es/isNotNil";
import React, { useCallback } from 'react';
import RadioButtonGroup from '../../../molecules/RadioButtonGroup';
import Tooltip from '../../../molecules/Tooltip';
import { useDefaultComponentValue } from '../../../utils/useDefaultComponentValue';
import RadioButton from '../../RadioButton';
const COMPONENT_NAME = 'Switch';
const Switch = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { id, name, value, tooltip, disabled, onChange, className, tabIndex = 0, containerClassName = 'Utils--inlineBlock', firstSetToOn = true } = props;
    const handleClick = useCallback((event)=>{
        if (onChange) {
            // if props value is null, then value depends on which side of Switch user clicks
            // -> check if it is RadioButton with class Radio--checked or not
            // otherwise new value is negation of previous value
            const newValue = value == null ? firstSetToOn ? !event.target.firstChild.className.includes('Switch--no') : event.target.firstChild.className.includes('Switch--yes') : !value;
            onChange(event, newValue);
        }
    }, [
        onChange,
        value,
        firstSetToOn
    ]);
    const handleKeyPressForValueChange = useCallback((event)=>{
        event.preventDefault();
        if (event.charCode === 32) {
            handleClick(event);
        }
    }, [
        handleClick
    ]);
    const withTooltip = useCallback((element)=>isNotNil(tooltip) ? /*#__PURE__*/ React.createElement(Tooltip, {
            hideIcon: true,
            place: "top",
            tip: element,
            effect: "solid",
            eventOff: "focusout",
            contentClassName: "Switch-tooltip"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "Tooltip-description"
        }, tooltip)) : element, [
        tooltip
    ]);
    return disabled ? withTooltip(/*#__PURE__*/ React.createElement("div", {
        id: id,
        className: cx('Switch Switch--typeIndicator', {
            'Switch--stateAgree': value === true
        }, {
            'Switch--stateDisagree': value === false
        }, className)
    })) : /*#__PURE__*/ React.createElement("div", {
        tabIndex: tabIndex,
        onClick: handleClick,
        className: containerClassName,
        onKeyPress: handleKeyPressForValueChange
    }, withTooltip(/*#__PURE__*/ React.createElement(RadioButtonGroup, {
        id: id,
        name: name,
        value: value ?? '',
        className: cx('Switch', {
            'Switch--stateAgree': value === true
        }, {
            'Switch--stateDisagree': value === false
        }, className)
    }, /*#__PURE__*/ React.createElement(RadioButton, {
        value: true,
        name: name,
        className: "Switch--yes",
        label: getLabelMessage('common:yes')
    }), value == null && /*#__PURE__*/ React.createElement(RadioButton, {
        value: undefined,
        name: name,
        className: "Switch--unknown",
        label: getLabelMessage('common:unknown')
    }), /*#__PURE__*/ React.createElement(RadioButton, {
        value: false,
        name: name,
        className: "Switch--no",
        label: getLabelMessage('common:no')
    }))));
};
Switch.propTypes = {
    /**
	 * Root className.
	 */ className: PropTypes.string,
    /**
	 * Classname of Switch wrapping div element
	 */ containerClassName: PropTypes.string,
    /**
	 * If true, the switch will be disabled.
	 */ disabled: PropTypes.bool,
    /**
	 * If true, the switch will set to ON after click in the middle from unset position.
	 */ firstSetToOn: PropTypes.bool,
    /**
	 * id attribute of RadioButtonGroup wrapping container
	 */ id: PropTypes.string,
    /**
	 * Name of the input field.
	 */ name: PropTypes.string,
    /**
	 * Callback function fired when state of the switch is changed.
	 *
	 * @param {object} event event targeting the element.
	 * @param {object} newValue new value.
	 * @return {undefined} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * tabindex html property of wrapping element
	 */ tabIndex: PropTypes.number,
    /**
	 * Tooltip content message
	 */ tooltip: PropTypes.string,
    /**
	 * Value of the field.
	 */ value: PropTypes.any
};
export default Switch;
